import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import MarkdownIt from "markdown-it";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import DocsSidebar from "@/components/app/DocsSidebar";
import VideoEmbed from "@/components/app/VideoEmbed";
import { Button } from "@/components/ui/button";
import { getDocBySlug, DOCS } from "@/content/docs";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";

const md = new MarkdownIt({ html: false, linkify: true, typographer: true });

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * /docs/:slug — Renders one help article from the static docs registry.
 * Falls back to a clean not-found state if the slug doesn't exist.
 */
export default function DocArticle() {
  const { slug } = useParams();
  const doc = getDocBySlug(slug);

  const html = useMemo(() => (doc ? md.render(doc.body) : ""), [doc]);

  // Adjacent docs (for prev / next nav at the bottom)
  const { prevDoc, nextDoc } = useMemo(() => {
    if (!doc) return { prevDoc: null, nextDoc: null };
    const idx = DOCS.findIndex((d) => d.slug === doc.slug);
    return {
      prevDoc: idx > 0 ? DOCS[idx - 1] : null,
      nextDoc: idx < DOCS.length - 1 ? DOCS[idx + 1] : null,
    };
  }, [doc]);

  useEffect(() => {
    if (doc) {
      document.title = `${doc.title} — ArloSEO docs`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && doc.excerpt) meta.setAttribute("content", doc.excerpt);
    }
  }, [doc]);

  if (!doc) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <MarketingNav />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="docs-article-not-found">
          <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
            <OtterIcon size={36} />
          </div>
          <h1 className="font-semibold tracking-tight text-3xl">Doc not found.</h1>
          <p className="mt-2 text-sm text-muted-foreground">Arlo couldn&apos;t fetch that one. Maybe try another?</p>
          <Link to="/docs" className="mt-6 inline-flex items-center gap-1.5 underline underline-offset-4">
            <ArrowLeft className="w-4 h-4" /> Back to docs
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20" data-testid="docs-article-page">
        <Link
          to="/docs"
          className="inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-widest text-muted-foreground hover:text-foreground"
          data-testid="docs-article-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All docs
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[18rem,1fr] gap-12">
          <DocsSidebar activeSlug={doc.slug} />

          <article className="min-w-0">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {doc.reading_minutes} min read</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> updated {formatDate(doc.updated)}</span>
            </div>

            {/* Title (we strip the leading H1 from body since the .md has one already) */}
            <h1
              className="font-semibold tracking-tight mt-4 text-4xl sm:text-5xl leading-[1.05]"
              data-testid="docs-article-title"
            >
              {doc.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {doc.excerpt}
            </p>

            <hr className="my-10 border-border" />

            {/* Optional video walkthrough — only renders when doc.videoUrl is set. */}
            {doc.videoUrl && (
              <VideoEmbed
                url={doc.videoUrl}
                title={doc.videoTitle || `${doc.title} · video walkthrough`}
              />
            )}

            {/* Body */}
            <div
              className="prose prose-zinc max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-h1:hidden prose-h2:text-2xl prose-h2:mt-12 prose-h3:text-xl prose-p:leading-relaxed prose-a:underline-offset-4 prose-li:leading-relaxed prose-table:text-sm"
              data-testid="docs-article-content"
              dangerouslySetInnerHTML={{ __html: html }}
            />

            {/* Prev / Next */}
            <div className="mt-16 pt-8 border-t border-border grid sm:grid-cols-2 gap-4" data-testid="docs-article-pager">
              {prevDoc ? (
                <Link
                  to={`/docs/${prevDoc.slug}`}
                  data-testid="docs-article-prev"
                  className="group block border border-border bg-white p-5 hover:border-foreground transition-colors"
                >
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Previous
                  </div>
                  <div className="font-semibold tracking-tight mt-2 group-hover:underline underline-offset-4">
                    {prevDoc.title}
                  </div>
                </Link>
              ) : <div />}
              {nextDoc ? (
                <Link
                  to={`/docs/${nextDoc.slug}`}
                  data-testid="docs-article-next"
                  className="group block border border-border bg-white p-5 hover:border-foreground transition-colors sm:text-right"
                >
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 sm:justify-end">
                    Next <ArrowRight className="w-3 h-3" />
                  </div>
                  <div className="font-semibold tracking-tight mt-2 group-hover:underline underline-offset-4">
                    {nextDoc.title}
                  </div>
                </Link>
              ) : <div />}
            </div>

            {/* CTA */}
            <div className="mt-12 border border-foreground bg-foreground text-background p-8 relative overflow-hidden" data-testid="docs-article-cta">
              <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
              <div className="relative">
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">
                  // ready to try it?
                </span>
                <h3 className="font-semibold tracking-tight text-2xl sm:text-3xl mt-3 leading-tight">
                  Spin up your first project with Arlo.
                </h3>
                <p className="mt-3 text-background/70 max-w-xl text-sm">
                  14‑day Pro trial, no card required. Audits, keyword tracking, and the Content Studio are all unlocked.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild className="rounded-none bg-background text-foreground hover:bg-background/90">
                    <Link to="/register" data-testid="docs-article-cta-signup">
                      Start free <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-none border-background text-background hover:bg-background hover:text-foreground">
                    <Link to="/docs" data-testid="docs-article-cta-more">More docs</Link>
                  </Button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
